import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '@/api/client';
import type { BehaviourQueryView } from '@/api/types';

const POLL_INTERVAL_MS = 2500;

/** i18n keys for the ways asking can be refused; anything else is a generic error. */
const ERROR_KEYS: Record<string, string> = {
  question_used: 'behaviour.err.used',
  question_in_progress: 'behaviour.err.inProgress',
  not_unlocked: 'behaviour.err.locked',
  rate_limited: 'behaviour.err.tooMany',
};

export interface UseBehaviourQueryResult {
  view?: BehaviourQueryView;
  loading: boolean;
  asking: boolean;
  /** An i18n key for the last failure to load or ask, or `null`. */
  error: string | null;
  ask: (query: string) => Promise<void>;
}

/**
 * The session's one behaviour question. Loads its state once, polls every 2.5 s while the plan is
 * being prepared, and stops the moment it is answered, escalated or handed back after a failure.
 */
export const useBehaviourQuery = (assessmentId: string | undefined): UseBehaviourQueryResult => {
  const [view, setView] = useState<BehaviourQueryView | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  const load = useCallback(async (): Promise<BehaviourQueryView | undefined> => {
    if (!assessmentId) return undefined;
    try {
      const next = await api.assessments.behaviourQuery(assessmentId);
      setView(next);
      return next;
    } catch {
      setError('common.error');
      return undefined;
    }
  }, [assessmentId]);

  const startPolling = useCallback(() => {
    if (timer.current) return;
    timer.current = setInterval(() => {
      void load().then((next) => {
        if (!next || next.status !== 'generating') stopPolling();
      });
    }, POLL_INTERVAL_MS);
  }, [load, stopPolling]);

  useEffect(() => {
    let cancelled = false;
    setView(undefined);
    setLoading(true);
    setError(null);
    stopPolling();
    void (async () => {
      const first = await load();
      if (cancelled) return;
      setLoading(false);
      if (first?.status === 'generating') startPolling();
    })();
    return () => {
      cancelled = true;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  const ask = useCallback(
    async (query: string) => {
      if (!assessmentId) return;
      setAsking(true);
      setError(null);
      try {
        const next = await api.assessments.askBehaviour(assessmentId, query);
        setView(next);
        if (next.status === 'generating') startPolling();
      } catch (err) {
        setError(err instanceof ApiError ? (ERROR_KEYS[err.code] ?? 'common.error') : 'common.error');
        // A refused ask usually means the state moved on (already answered, already running).
        const current = await load();
        if (current?.status === 'generating') startPolling();
      } finally {
        setAsking(false);
      }
    },
    [assessmentId, load, startPolling],
  );

  return { view, loading, asking, error, ask };
};
